import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { ToastUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { ApiAction, createApiAction } from 'api'
import { ErrorUtils } from 'utils'
import { getRequirementDetails } from 'core/completion/api/completionService'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getSelfAssessmentDetails,
  uploadAssessmentDocument,
  removeAssessmentDocument,
  performSelfAssessment,
} from 'modules/activityDetails/SelfAssessmentDetails/api/selfAssessmentDetailsService'
import {
  AssessStatus,
  SelfAssessmentParams,
} from 'modules/activityDetails/SelfAssessmentDetails/types'
import {
  SelfAssessmentDetailsState,
  ApiSelfAssessmentDetailsResponse,
  ApiSelfAssessmentUploadDocumentResponse,
  ApiSelfAssessmentRemoveDocumentResponse,
  ApiSelfAssessmentPerformResponse,
} from './types'

const initialState: SelfAssessmentDetailsState = {
  selfAssessmentDetailsData: {},
  selfAssessmentDocument: {},
  selfAssessmentDocumentRemoved: {},
  selfAssessmentFormData: {
    assess: AssessStatus.Approved,
    comment: '',
  },
  selfAssessmentPerform: {},
}

const selfAssessmentDetailsSlice = createSlice({
  name: 'selfAssessmentDetails',
  initialState,
  reducers: {
    selfAssessmentDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSelfAssessmentDetailsResponse>>
    ) {
      state.selfAssessmentDetailsData = action.payload
    },
    clearSelfAssessmentDetails(state) {
      state.selfAssessmentDetailsData = initialState.selfAssessmentDetailsData
    },
    selfAssessmentDocumentUploaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSelfAssessmentUploadDocumentResponse>>
    ) {
      state.selfAssessmentDocument = action.payload
    },
    selfAssessmentDocumentRemoveProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSelfAssessmentRemoveDocumentResponse>>
    ) {
      state.selfAssessmentDocumentRemoved = action.payload
    },
    setSelfAssessmentFormValues(state, action: PayloadAction<SelfAssessmentParams>) {
      state.selfAssessmentFormData = { ...state.selfAssessmentFormData, ...action.payload }
    },
    resetSelfAssessmentFormValues(state) {
      state.selfAssessmentFormData = initialState.selfAssessmentFormData
    },
    selfAssessmentPerformProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSelfAssessmentPerformResponse>>
    ) {
      state.selfAssessmentPerform = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  selfAssessmentDetailsLoaded,
  selfAssessmentDocumentUploaded,
  selfAssessmentDocumentRemoveProcessed,
  selfAssessmentPerformProcessed,
} = selfAssessmentDetailsSlice.actions

export const {
  clearSelfAssessmentDetails,
  setSelfAssessmentFormValues,
  resetSelfAssessmentFormValues,
} = selfAssessmentDetailsSlice.actions
export default selfAssessmentDetailsSlice.reducer

export const loadSelfAssessmentDetailsData = (competenceId: Guid, assessmentId: Guid) => {
  return createApiAction({
    apiCalls: {
      completionDetails: () => getRequirementDetails(competenceId),
      selfAssessmentDetails: () => getSelfAssessmentDetails(assessmentId),
    },
    payloadAction: selfAssessmentDetailsLoaded,
  })
}

export const reloadSelfAssessmentDetails = (competenceId: Guid, assessmentId: Guid) => {
  return createApiAction({
    apiCalls: {
      completionDetails: () => getRequirementDetails(competenceId),
      selfAssessmentDetails: () => getSelfAssessmentDetails(assessmentId),
    },
    payloadAction: selfAssessmentDetailsLoaded,
    shouldShowGlobalLoading: true,
    getResultToKeep: ({ selfAssessmentDetails }) =>
      selfAssessmentDetails.selfAssessmentDetailsData.result,
  })
}

const handleReloadSelfAssessmentDetails =
  (response: ApiMiddlewarePayload, payloadAction: ApiAction['payloadAction']): AppThunk =>
  async (dispatch, getState) => {
    dispatch(payloadAction(response))

    if (!response.error) {
      const { selfAssessmentDetails } = getState()
      const { completionDetails, selfAssessmentDetails: details } =
        selfAssessmentDetails.selfAssessmentDetailsData?.result || {}
      const { id: competenceId } = completionDetails || {}
      const { id: assessmentId } = details || {}
      competenceId &&
        assessmentId &&
        dispatch(reloadSelfAssessmentDetails(competenceId, assessmentId))
    }
  }

export const uploadSelfAssessmentDocument = (activityId: Guid, files: File[]) => {
  return createApiAction({
    apiCalls: {
      assessmentDocument: () => uploadAssessmentDocument(activityId, files),
    },
    payloadAction: selfAssessmentDocumentUploaded,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleReloadSelfAssessmentDetails,
      params: selfAssessmentDocumentUploaded,
    },
  })
}

export const deleteSelfAssessmentDocument = (documentId: Guid) => {
  return createApiAction({
    apiCalls: {
      assessmentDocumentRemoved: () => removeAssessmentDocument(documentId),
    },
    payloadAction: selfAssessmentDocumentRemoveProcessed,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleReloadSelfAssessmentDetails,
      params: selfAssessmentDocumentRemoveProcessed,
    },
  })
}

const handleAssessmentPerform =
  (response: ApiMiddlewarePayload<ApiSelfAssessmentPerformResponse>): AppThunk =>
  async (dispatch, getState) => {
    dispatch(selfAssessmentPerformProcessed(response))
    const { error } = response

    if (error) {
      const { code, details, error: errorMessage } = error
      const isCommentValidationErrorCode =
        code === 'InvalidDataValueResponse' && details === 'Comment'
      const CUSTOM_COMMENT_ERROR = 'InvalidDataValueResponseSelfAssessmentCommentRequired'
      const resultErrorMessage = isCommentValidationErrorCode
        ? ErrorUtils.getErrorMessage({ code: CUSTOM_COMMENT_ERROR, message: errorMessage })
        : ErrorUtils.getErrorMessage({ code, message: errorMessage, details })
      ToastUtils.error(resultErrorMessage)
      return
    }

    // Reset form data on perform success
    dispatch(resetSelfAssessmentFormValues())

    const { selfAssessmentDetails } = getState()
    const { completionDetails, selfAssessmentDetails: details } =
      selfAssessmentDetails.selfAssessmentDetailsData?.result || {}
    const { id: competenceId } = completionDetails || {}
    const { id: assessmentId } = details || {}
    competenceId &&
      assessmentId &&
      dispatch(reloadSelfAssessmentDetails(competenceId, assessmentId))
  }

export const performAssessment = (values: SelfAssessmentParams, activityId: Guid) => {
  return createApiAction({
    apiCalls: {
      assessmentPerformed: () => performSelfAssessment(values, activityId),
    },
    payloadAction: selfAssessmentPerformProcessed,
    nextAction: handleAssessmentPerform,
    shouldShowGlobalLoading: true,
  })
}
